import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSigningDocumentsQuery } from '@/features/signing-documents/queries'
import { documentDate } from '@/features/signing-documents/model'
import {
  DOCUMENTS_TABLE_ID,
  SigningStatus,
} from '@/features/signing-documents/table'
import { useShellHref } from '@/utils/router'
import { SHEET_TABLE_CELL, SHEET_TABLE_HEAD } from './sheetTableStyles'

export function ClientDocumentsTab({ memberId }: { memberId: string }) {
  const query = useSigningDocumentsQuery(memberId)
  const shellHref = useShellHref()
  const documents = (query.data ?? [])
    .filter((document) => document.memberId === memberId)
    .toSorted((a, b) => b.createdAt.localeCompare(a.createdAt))
  if (query.isPending)
    return (
      <div aria-label="Loading documents" className="flex flex-col gap-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  if (query.isError)
    return (
      <div
        role="alert"
        className="py-8 text-center text-sm text-muted-foreground">
        <p>Could not load documents.</p>
        <Button variant="ghost" size="sm" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    )
  if (!documents.length)
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No documents on file
      </p>
    )
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className={SHEET_TABLE_HEAD}>Document</TableHead>
          <TableHead className={SHEET_TABLE_HEAD}>Status</TableHead>
          <TableHead className={SHEET_TABLE_HEAD}>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={document.id} className="relative cursor-pointer">
            <TableCell className={SHEET_TABLE_CELL}>
              <Link
                to={shellHref(
                  `/documents?${new URLSearchParams({ [`${DOCUMENTS_TABLE_ID}.uuid`]: document.id })}`
                )}
                aria-label={`Open ${document.formName} · ${document.configurationName}`}
                className="rounded-sm font-medium after:absolute after:inset-0 focus-visible:ring-2 focus-visible:ring-primary-solid focus-visible:outline-none">
                {document.formName}
              </Link>
              <p className="text-xs text-muted-foreground">
                {document.configurationName}
              </p>
            </TableCell>
            <TableCell className={SHEET_TABLE_CELL}>
              <SigningStatus state={document.state} />
            </TableCell>
            <TableCell
              className={`${SHEET_TABLE_CELL} whitespace-nowrap text-xs text-muted-foreground`}>
              {documentDate(document.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
